/**
 * StorageOverviewCard — Speicher-Übersicht pro Firma + Aufräum-Assistent für unbenutzte Dateien.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { HardDrive, Loader2, Trash2, Image as ImageIcon, Film, Shrink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const fmtBytes = (b) => {
  if (!b) return "0 MB";
  const mb = b / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
};

export default function StorageOverviewCard({ refreshKey, onCleaned }) {
  const [stats, setStats] = useState(null);
  const [cleanTarget, setCleanTarget] = useState(null); // company row
  const [cleaning, setCleaning] = useState(false);
  const [compressing, setCompressing] = useState({}); // company_id -> {done,total}

  const pollCompress = (cid) => {
    const iv = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/media/compress/status`, { params: { company_id: cid }, withCredentials: true });
        const s = r.data;
        if (s.status === "running") {
          setCompressing((p) => ({ ...p, [cid]: { done: s.done, total: s.total } }));
        } else {
          clearInterval(iv);
          setCompressing((p) => { const n = { ...p }; delete n[cid]; return n; });
          if (s.status === "done") {
            toast.success(`Medien verkleinert — ${fmtBytes(s.saved_bytes)} gespart ✨`, { duration: 9000 });
          } else if (s.status === "error") {
            toast.error("Verkleinerung abgebrochen — bitte erneut versuchen");
          }
          load();
          onCleaned && onCleaned();
        }
      } catch { clearInterval(iv); }
    }, 3000);
  };

  const startCompress = async (c) => {
    try {
      const r = await axios.post(`${API}/media/compress`, { company_id: c.company_id }, { withCredentials: true });
      toast.info(r.data.message, { duration: 6000 });
      if (r.data.total > 0) {
        setCompressing((p) => ({ ...p, [c.company_id]: { done: 0, total: r.data.total } }));
        pollCompress(c.company_id);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Start fehlgeschlagen"); }
  };

  const load = useCallback(() => {
    axios.get(`${API}/media/storage-stats`, { withCredentials: true })
      .then((r) => setStats(r.data)).catch(() => setStats({ total: {}, companies: [] }));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const runCleanup = async () => {
    if (!cleanTarget) return;
    setCleaning(true);
    try {
      const r = await axios.post(`${API}/media/cleanup`, { company_id: cleanTarget.company_id }, { withCredentials: true });
      toast.success(r.data.message, { duration: 7000 });
      setCleanTarget(null);
      load();
      onCleaned && onCleaned();
    } catch (e) { toast.error(e.response?.data?.detail || "Aufräumen fehlgeschlagen"); }
    finally { setCleaning(false); }
  };

  if (!stats) {
    return (
      <Card data-testid="storage-overview-card">
        <CardContent className="py-6 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></CardContent>
      </Card>
    );
  }
  const t = stats.total || {};
  const maxBytes = Math.max(...(stats.companies || []).map((c) => c.bytes), 1);

  return (
    <Card data-testid="storage-overview-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex flex-wrap items-center gap-2">
          <HardDrive className="w-4 h-4 text-blue-600" /> Speicher-Übersicht
          <Badge variant="outline" className="text-[10px]" data-testid="storage-total-badge">
            {fmtBytes(t.bytes)} gesamt · {t.files || 0} Dateien
          </Badge>
          <Badge variant="outline" className="text-[10px]"><ImageIcon className="w-2.5 h-2.5 mr-0.5" /> {t.images || 0} Bilder</Badge>
          <Badge variant="outline" className="text-[10px]"><Film className="w-2.5 h-2.5 mr-0.5" /> {t.videos || 0} Videos</Badge>
          {(t.unused_count || 0) > 0 && (
            <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]" data-testid="storage-unused-badge">
              {t.unused_count} unbenutzt ({fmtBytes(t.unused_bytes)})
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {(stats.companies || []).map((c) => (
          <div key={c.company_id} className="flex items-center gap-3 text-xs" data-testid={`storage-row-${c.company_id}`}>
            <span className="w-36 truncate font-medium">{c.company_name}</span>
            <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden flex">
              <div className="h-full bg-blue-500" style={{ width: `${(c.image_bytes / maxBytes) * 100}%` }} title={`Bilder: ${fmtBytes(c.image_bytes)}`} />
              <div className="h-full bg-violet-500" style={{ width: `${(c.video_bytes / maxBytes) * 100}%` }} title={`Videos: ${fmtBytes(c.video_bytes)}`} />
            </div>
            <span className="w-20 text-right text-gray-600">{fmtBytes(c.bytes)}</span>
            <span className="w-24 text-gray-400">{c.images} 🖼 · {c.videos} 🎬</span>
            {compressing[c.company_id] ? (
              <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]" data-testid={`compress-progress-${c.company_id}`}>
                <Loader2 className="w-2.5 h-2.5 mr-1 animate-spin" />
                verkleinere {compressing[c.company_id].done}/{compressing[c.company_id].total}…
              </Badge>
            ) : (c.image_bytes > 5 * 1024 * 1024 || c.video_bytes > 20 * 1024 * 1024) ? (
              <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 text-blue-700 border-blue-300 hover:bg-blue-50"
                onClick={() => startCompress(c)} data-testid={`compress-btn-${c.company_id}`}
                title="Verkleinert alle grossen Bilder (max 2048px) und Videos (max 1920px, H.264) auf Social-Media-Qualität — Posts & Anzeigen bleiben intakt">
                <Shrink className="w-2.5 h-2.5" /> Verkleinern
              </Button>
            ) : null}
            {c.unused_count > 0 ? (
              <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={() => setCleanTarget(c)} data-testid={`cleanup-btn-${c.company_id}`}>
                <Trash2 className="w-2.5 h-2.5" /> {c.unused_count} aufräumen ({fmtBytes(c.unused_bytes)})
              </Button>
            ) : (
              <span className="text-[10px] text-emerald-600 w-28">✓ alles in Nutzung</span>
            )}
          </div>
        ))}
        <p className="text-[10px] text-gray-400 pt-1">
          Dateien in Posts, Brand-Kits oder offenen Ad-Freigaben sind geschützt und werden beim Aufräumen nie gelöscht.
        </p>
      </CardContent>

      <AlertDialog open={!!cleanTarget} onOpenChange={(o) => !o && setCleanTarget(null)}>
        <AlertDialogContent data-testid="cleanup-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Unbenutzte Dateien löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Bei «{cleanTarget?.company_name}» werden <b>{cleanTarget?.unused_count} unbenutzte Datei(en)</b> gelöscht
              und <b>{fmtBytes(cleanTarget?.unused_bytes)}</b> freigegeben.
              Dateien, die in Posts, Brand-Kits oder Ad-Freigaben verwendet werden, bleiben erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cleanup-cancel-btn">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={runCleanup} disabled={cleaning} className="bg-red-600 hover:bg-red-700" data-testid="cleanup-confirm-btn">
              {cleaning ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Jetzt aufräumen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
